import React, { useState } from 'react';
import { MapPin, AlertTriangle, CheckCircle } from 'lucide-react';
import { useGeolocationContext } from '../../contexts/GeolocationContext';
import { useMobile } from '../../hooks/useMobile';
import toast from 'react-hot-toast';

export const GeolocationStatus: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { ubicacionActual, errorUbicacion, startLocationTracking, stopLocationTracking } = useGeolocationContext();
  const isMobile = useMobile();

  // Removidos los logs innecesarios

  const handleClick = async () => {
    if (isLoading) return; // Prevenir múltiples clicks
    
    setIsLoading(true);
    
    const loadingToast = toast.loading('Activando ubicación...');
    
    try {
      await startLocationTracking();
      toast.success('Ubicación activada correctamente', { id: loadingToast });
    } catch (error) {
      toast.error('Error al activar la ubicación', { id: loadingToast });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      stopLocationTracking();
      toast.success('Ubicación desactivada');
    } catch (error) {
      toast.error('Error al desactivar ubicación');
    } finally {
      setIsLoading(false);
    }
  };

  let content;

  if (ubicacionActual) {
    content = (
      <button
        onClick={handleDeactivate}
        disabled={isLoading}
        className={`flex items-center justify-between w-full p-3 rounded-lg ${
          isLoading
            ? 'bg-gray-100 dark:bg-gray-800 cursor-wait'
            : 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'
        }`}
      >
        <div className="flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <span className="text-sm font-medium text-green-700 dark:text-green-300">
            {isLoading ? 'Desactivando...' : 'Ubicación activa'}
          </span>
        </div>
        {!isLoading && (
          <span className="text-xs text-green-600 dark:text-green-400">
            Desactivar
          </span>
        )}
      </button>
    );
  } else if (errorUbicacion) {
    content = (
      <button
        onClick={handleClick}
        className="flex items-center justify-between w-full p-3 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50"
      >
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span className="text-sm font-medium text-red-700 dark:text-red-300">
            {errorUbicacion === "No se otorgó permiso para acceder a la ubicación."
              ? "Permiso denegado"
              : "Error de ubicación"}
          </span>
        </div>
        <span className="text-xs text-red-600 dark:text-red-400">
          Reintentar
        </span>
      </button>
    );
  } else {
    content = (
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`flex items-center justify-between w-full p-3 rounded-lg ${
          isLoading
            ? 'bg-gray-100 dark:bg-gray-800 cursor-wait'
            : 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50'
        }`}
      >
        <div className="flex items-center space-x-2">
          <MapPin className={`h-5 w-5 ${
            isLoading ? 'text-gray-500 dark:text-gray-400' : 'text-blue-600 dark:text-blue-400'
          }`} />
          <span className={`text-sm font-medium ${
            isLoading 
              ? 'text-gray-600 dark:text-gray-300' 
              : 'text-blue-700 dark:text-blue-300'
          }`}>
            {isLoading 
              ? 'Activando...' 
              : isMobile ? 'Ubicación inactiva' : 'Activar ubicación'}
          </span>
        </div>
        {!isLoading && (
          <span className="text-xs text-blue-600 dark:text-blue-400">
            Activar
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="px-3">
      {content}
    </div>
  );
};